import { useState, useEffect } from 'react';
import { X, TrendingUp, MessageCircle, ArrowRight, CheckCircle, Clock, XCircle, Loader2 } from 'lucide-react';

interface AuthModalProps {
  mode: 'login' | 'signup' | null;
  onClose: () => void;
  onLoginSuccess?: () => void;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const TELEGRAM_BOT_USERNAME = 'GOTRADINGZY_bot';

type OTPStatus = 'idle' | 'checking' | 'pending' | 'approved' | 'rejected' | 'verifying' | 'success' | 'error';

export const AuthModal = ({ mode, onClose, onLoginSuccess }: AuthModalProps) => {
  const [otpCode, setOtpCode] = useState('');
  const [status, setStatus] = useState<OTPStatus>('idle');
  const [error, setError] = useState('');
  const [showOTPInput, setShowOTPInput] = useState(false);

  useEffect(() => {
    if (mode) {
      document.body.style.overflow = 'hidden';
      setOtpCode('');
      setStatus('idle');
      setError('');
      setShowOTPInput(false);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mode]);

  if (!mode) return null;

  const handleOpenTelegram = () => {
    window.open(`https://t.me/${TELEGRAM_BOT_USERNAME}`, '_blank');
    setShowOTPInput(true);
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setError('Masukkan kode OTP 6 digit');
      return;
    }

    setStatus('verifying');
    setError('');

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/telegram-bot/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp_code: otpCode })
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        localStorage.setItem('gotradingzy_telegram_user', JSON.stringify({
          telegram_chat_id: data.telegram_chat_id,
          telegram_username: data.telegram_username,
          logged_in_at: new Date().toISOString()
        }));

        setTimeout(() => {
          if (onLoginSuccess) onLoginSuccess();
          onClose();
          window.location.reload();
        }, 1500);
      } else {
        setStatus('error');
        setError(data.message || 'OTP tidak valid atau belum disetujui admin');
      }
    } catch (err) {
      setStatus('error');
      setError('Terjadi kesalahan. Silakan coba lagi.');
    }
  };

  const statusConfig = {
    idle: { icon: MessageCircle, color: 'text-slate-400', bg: 'bg-slate-500/10' },
    checking: { icon: Loader2, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    pending: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    approved: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    rejected: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
    verifying: { icon: Loader2, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    success: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    error: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
      <div className="bg-[#0a0e17] border border-slate-800 rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Login dengan Telegram</h2>
                <p className="text-sm text-slate-500">GOTRADINGZY</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Login Berhasil!</h3>
              <p className="text-slate-400">Mengalihkan ke dashboard...</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-cyan-400 font-bold text-sm">1</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">Buka Bot Telegram</h4>
                    <p className="text-sm text-slate-400 mb-3">Klik tombol di bawah untuk membuka bot GOTRADINGZY di Telegram</p>
                    <button
                      onClick={handleOpenTelegram}
                      className="flex items-center space-x-2 px-4 py-2 bg-[#0088cc] hover:bg-[#0077b5] text-white rounded-lg font-medium transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>Buka Telegram Bot</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-cyan-400 font-bold text-sm">2</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">Minta Kode OTP</h4>
                    <p className="text-sm text-slate-400">Ketik <code className="px-1.5 py-0.5 bg-slate-700 rounded text-cyan-400">/login</code> di bot untuk meminta kode OTP. Admin akan menyetujui permintaan Anda.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-cyan-400 font-bold text-sm">3</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">Masukkan Kode OTP</h4>
                    <p className="text-sm text-slate-400 mb-3">Setelah admin menyetujui, masukkan kode OTP yang dikirim ke Telegram Anda</p>

                    {showOTPInput && (
                      <div className="space-y-3">
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            maxLength={6}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            placeholder="000000"
                            className="flex-1 input-field text-center text-2xl tracking-[0.5em] font-mono"
                            disabled={status === 'verifying'}
                          />
                        </div>
                        <button
                          onClick={handleVerifyOTP}
                          disabled={status === 'verifying' || otpCode.length !== 6}
                          className="w-full btn-primary py-3 disabled:opacity-50 flex items-center justify-center space-x-2"
                        >
                          {status === 'verifying' ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Memverifikasi...</span>
                            </>
                          ) : (
                            <span>Verifikasi OTP</span>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <div className="pt-4 border-t border-slate-800">
                <p className="text-xs text-slate-500 text-center">
                  Login memerlukan persetujuan admin untuk keamanan akun Anda
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
